import { ExcelFormulas } from "@bn-monorepo/js-packages/src/Helpers/calculators/functions";
export interface RepaymentPlanYear {
  year: number;
  total: number;
  outstanding: number;
  months: RepaymentPlanMonth[];
}
export interface RepaymentPlanMonth {
  monthNum: number;
  interestPct: number;
  interestAmount: number;
  repaymentAmount: number;
  totalAmount: number;
  installmentFee: number;
}
export interface CalculateLoanResult {
  years: RepaymentPlanYear[];
  amount: number;
  interestSum: number;
  capitalSum: number;
  sum: number;
  effectiveInterest: number;
}

export enum LoanType {
  Annuity = 1,
  Serial = 2,
}

export interface LoanParams {
  loanType: LoanType;
  loanAmount: number;
  numberOfMonths: number;
  interestRate: number;
  setupFee: number;
  installmentFee: number;
  addFeeToLoanAmount: boolean;
}

export const calculateLoan = (loanParams: LoanParams): CalculateLoanResult => {
  const loanAmount = loanParams.addFeeToLoanAmount
    ? loanParams.loanAmount + loanParams.setupFee
    : loanParams.loanAmount;

  const round = (num: number, decimals: number): number => {
    return Math.round((num + Number.EPSILON) * 10 ** decimals) / 10 ** decimals;
  };

  const interestPrMonth = loanParams.interestRate / 100 / 12;
  const today = new Date();
  const getStartYear = (): number => (today.getMonth() === 11 ? today.getFullYear() + 1 : today.getFullYear());
  let firstYear = getStartYear();
  const startMonth = today.getMonth() === 11 ? 0 : today.getMonth() + 1;
  const years: RepaymentPlanYear[] = [];
  let months: RepaymentPlanMonth[] = [];
  let yearTotal = 0;
  let interestSum = 0;
  let totalCost = 0;
  let monthCounter = startMonth + 1;
  let outstandingAmount = loanAmount;
  const cashFlow = new Array<number>();

  cashFlow.push(-(loanAmount - loanParams.setupFee));

  //Serial loan:
  if (loanParams.loanType === LoanType.Serial) {
    const repaymentAmount = loanAmount / loanParams.numberOfMonths;

    for (let i = 0; i < loanParams.numberOfMonths; i++) {
      const interestAmount = outstandingAmount * interestPrMonth;
      const monthlyPayment = loanParams.installmentFee + interestAmount + repaymentAmount;
      yearTotal += monthlyPayment;
      interestSum += interestAmount;
      totalCost += monthlyPayment;
      outstandingAmount -= repaymentAmount;

      const month: RepaymentPlanMonth = {
        monthNum: monthCounter,
        interestAmount: interestAmount,
        repaymentAmount: repaymentAmount,
        totalAmount: monthlyPayment,
        interestPct: loanParams.interestRate,
        installmentFee: loanParams.installmentFee,
      };

      months.push(month);

      if (monthCounter === 12 || i === loanParams.numberOfMonths - 1) {
        years.push({
          year: firstYear,
          total: yearTotal,
          outstanding: i === loanParams.numberOfMonths - 1 ? 0 : outstandingAmount,
          months: months,
        });
        months = [];
        yearTotal = 0;
        monthCounter = 1;
        firstYear++;
      } else {
        monthCounter++;
      }

      cashFlow.push(monthlyPayment);
    }
  } else {
    //Annuity loan:
    const monthlyPayment =
      loanParams.installmentFee + ExcelFormulas.PMT(interestPrMonth, loanParams.numberOfMonths, -loanAmount, 0, 0);
    for (let i = 0; i < loanParams.numberOfMonths; i++) {
      yearTotal += monthlyPayment;
      const repaymentAmount = ExcelFormulas.PPMT(interestPrMonth, i + 1, loanParams.numberOfMonths, -loanAmount, 0, 0);
      const interestAmount = monthlyPayment - loanParams.installmentFee - repaymentAmount;
      interestSum += interestAmount;
      totalCost += monthlyPayment;
      outstandingAmount -= repaymentAmount;

      const month: RepaymentPlanMonth = {
        monthNum: monthCounter,
        interestAmount: interestAmount,
        repaymentAmount: repaymentAmount,
        totalAmount: monthlyPayment,
        interestPct: loanParams.interestRate,
        installmentFee: loanParams.installmentFee,
      };

      months.push(month);

      if (monthCounter === 12 || i === loanParams.numberOfMonths - 1) {
        years.push({
          year: firstYear,
          total: yearTotal,
          outstanding: i === loanParams.numberOfMonths - 1 ? 0 : outstandingAmount,
          months: months,
        });
        months = [];
        yearTotal = 0;
        monthCounter = 1;
        firstYear++;
      } else {
        monthCounter++;
      }
    }

    for (let i = 0; i < loanParams.numberOfMonths; i++) {
      cashFlow.push(monthlyPayment);
    }
  }
  //Common for both loan types:
  const rate = ExcelFormulas.XIRR(cashFlow);

  return {
    years: years,
    amount: loanParams.loanAmount,
    interestSum: interestSum,
    capitalSum: interestSum + loanParams.setupFee + loanParams.installmentFee * loanParams.numberOfMonths,
    sum: totalCost,
    effectiveInterest: round(rate * 100, 2),
  };
};
